import { useForm } from "@mantine/form";
import { zodResolver } from "mantine-form-zod-resolver";
import { z } from "zod";
import {
  PronosticV2,
  PronosticV2Element,
  isInputLikeElement,
} from "pronosticsv2/types/pronosticv2.model";
import { Button, Text } from "@mantine/core";
import React from "react";
import { useUpdatePlayerMutation } from "quiz/api/players.endpoints";
import type { Player } from "quiz/types/player.model";
import { renderWithLineBreaks } from "common/utils/renderWithLineBreaks";
import { PronosticV2Service } from "services/pronosticv2.service";
import { CarouselInput } from "common/components/Carousel";

interface PlayPronosticsV2FormProps {
  pronosticV2: PronosticV2;
  setScreen: () => void;

  subPlace: string;
  playerInfo: Partial<Player>;
  playerId: string;
  partitionId: string;
}

// Strict validation: field must not be empty and must be a natural number
// const naturalNumberString = z
//   .string()
//   .min(1, "All fields are mandatory")
//   .regex(/^\d+$/, "Must be a natural number (0 or greater)");

const createPredictionSchema = (
  pronostics: PronosticV2["commonSettings"]["general"]["moduleSettings"]["pronostics"],
) => {
  return z.object({
    predictions: z
      .array(
        z.object({
          value1: z.string().min(1, "All fields are mandatory"),
          value2: z.string().optional(),
        }),
      )
      .length(pronostics.length)
      .superRefine((predictions, ctx) => {
        predictions.forEach((pred, index) => {
          const prono = pronostics[index];
          const isInputLike = isInputLikeElement(prono);
          const isThirdPrediction = index === 2;
          const needsValue2 = isInputLike && !!prono.element5FieldSize;

          // Validate value1 based on element type
          if (isInputLike) {
            // For input elements, value1 must be a natural number
            if (!/^\d+$/.test(pred.value1)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Must be a natural number (0 or greater)",
                path: [index, "value1"],
              });
            }
          }
          // For slideshow elements, value1 is a player name (string) - no numeric validation needed

          // Validate value2 for input elements that need it
          if (needsValue2 && !isThirdPrediction) {
            if (!pred.value2 || !/^\d+$/.test(pred.value2)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "All fields are mandatory",
                path: [index, "value2"],
              });
            }
          } else if (needsValue2 && isThirdPrediction && pred.value2) {
            if (!/^\d+$/.test(pred.value2)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                // message: "Must be a natural number (0 or greater)",
                path: [index, "value2"],
              });
            }
          }
        });
      }),
  });
};

export function isValidUrl(string: string) {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
}

export const PlayPronosticV2Form: React.FC<PlayPronosticsV2FormProps> = ({
  pronosticV2,
  setScreen,
  subPlace,
  playerInfo,
  playerId,
  partitionId,
}) => {
  const [updatePlayer, { isLoading }] = useUpdatePlayerMutation();
  void updatePlayer;

  const schema = createPredictionSchema(
    pronosticV2.commonSettings.general.moduleSettings.pronostics,
  );

  const form = useForm({
    initialValues: {
      predictions:
        pronosticV2.commonSettings.general.moduleSettings.pronostics.map(
          (prono) => {
            const isInputLike = isInputLikeElement(prono);
            // For slideshow elements, initialize with first player's name
            // For input elements, initialize with empty string
            const initialValue1 = isInputLike
              ? ""
              : prono.player && prono.player.length > 0
                ? prono.player[0].name
                : "";
            return {
              value1: initialValue1,
              value2: "",
            };
          },
        ),
    },
    validate: zodResolver(schema),
  });
  // console.log(partitionId.split("$")[1]);
  const handleSubmit = async (values: z.infer<typeof schema>) => {
    const playerUpdatePayload: Partial<Player> = {
      ...playerInfo,
      pronosticInfo: {
        pronosticV2Id: pronosticV2.id,
        subPlace: partitionId.split("$")[1],
        prediction: values.predictions.map((pred, index) => {
          const currentPronostic =
            pronosticV2.commonSettings.general.moduleSettings.pronostics[index];
          const isInputLike = isInputLikeElement(currentPronostic);

          // For input elements, convert to number; for slideshow elements, keep as string
          const value1 = isInputLike ? Number(pred.value1) : pred.value1;

          const predictionEntry = {
            index: currentPronostic.index,
            values: [value1],
          };

          if (
            isInputLike &&
            currentPronostic.element5FieldSize &&
            pred.value2 !== null
          ) {
            predictionEntry.values.push(Number(pred.value2));
          }

          return predictionEntry;
        }),
      },
    };

    const service = new PronosticV2Service();

    try {
      const response = await service.submitPronosticV2Results({
        playerData: playerUpdatePayload as any,
      });
      console.log("Player updated successfully", response);
      setScreen();
    } catch (error: any) {
      if (error.message === "LATE_SUBMISSION") {
        form.setErrors({
          form: "Le pronostic est terminé. Les soumissions ne sont plus acceptées.",
        });
        window.location.reload();
        console.error("Failed to update player:", error);
      } else {
        form.setErrors({ form: "Update failed. Please try again." });
      }
    }
  };

  return (
    <form
      onSubmit={form.onSubmit(handleSubmit)}
      className="w-full flex flex-col items-center gap-4 mb-4"
    >
      <h1
        style={{
          fontSize:
            pronosticV2.commonSettings.webApp.moduleSettings.messageTitleSize,
          color:
            pronosticV2.commonSettings.webApp.moduleSettings.messageTitleColor,
          padding: "0 10px",
        }}
        className="text-center"
      >
        {renderWithLineBreaks(
          pronosticV2.commonSettings.webApp.texts.welcomeMessage,
        )}
      </h1>

      {form.errors.form && (
        <Text color="red" size="sm">
          {form.errors.form}
        </Text>
      )}

      <div>
        {(() => {
          const pronostics =
            pronosticV2.commonSettings.general.moduleSettings.pronostics;
          type RenderItem =
            | {
                type: "single";
                pronostic: (typeof pronostics)[number];
                index: number;
              }
            | {
                type: "pair";
                first: (typeof pronostics)[number];
                second: (typeof pronostics)[number];
                firstIndex: number;
                secondIndex: number;
              };
          const groupedItems: RenderItem[] = [];
          let i = 0;
          while (i < pronostics.length) {
            const current = pronostics[i];
            const next = pronostics[i + 1];
            if (
              isInputLikeElement(current) &&
              current.element5FieldSize === 0 &&
              next &&
              isInputLikeElement(next) &&
              next.element5FieldSize === 0 &&
              current.element1Title === next.element1Title
            ) {
              groupedItems.push({
                type: "pair",
                first: current,
                second: next,
                firstIndex: i,
                secondIndex: i + 1,
              });
              i += 2;
            } else {
              groupedItems.push({
                type: "single",
                pronostic: current,
                index: i,
              });
              i += 1;
            }
          }

          return groupedItems.map((item) => {
            if (item.type === "single") {
              const prono = item.pronostic;
              const index = item.index;
              return (
                <React.Fragment key={prono.index}>
                  <div className="flex flex-col justify-center items-start gap-4 ml-4 mt-8">
                    <h1
                      style={{
                        fontSize:
                          pronosticV2.commonSettings.webApp.moduleSettings
                            .pronoTitleSize,
                        color:
                          pronosticV2.commonSettings.webApp.moduleSettings
                            .pronoTitleColor,
                      }}
                    >
                      {isInputLikeElement(prono)
                        ? prono.element1Title
                        : (prono as any).title}
                    </h1>
                  </div>

                  <div className="flex flex-col justify-center items-center gap-4 mt-8">
                    {isInputLikeElement(prono) ? (
                      <div className="flex items-center gap-5 mx-2">
                        {/* Left Visual */}
                        <div className="flex flex-col w-[100px] mt-auto">
                          {isValidUrl(prono.element2Visual) &&
                          prono.element2Visual ? (
                            <img
                              src={prono.element2Visual}
                              alt=""
                              className="mx-auto"
                              style={{
                                width: "120px",
                                height: "124px",
                                objectFit: "contain",
                                zIndex: 0,
                              }}
                            />
                          ) : null}
                          <span className="text-center">
                            {renderWithLineBreaks(prono.element3Title)}
                          </span>
                        </div>

                        {/* First Input */}
                        <div className="flex flex-col items-center">
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            {...form.getInputProps(
                              `predictions.${index}.value1`,
                            )}
                            className="text-slate-900 text-center"
                            style={{
                              width: 50,
                              height: 50,
                              fontSize: 35,
                              textAlign: "center",
                            }}
                            maxLength={prono.element4FieldSize}
                          />
                        </div>

                        {/* Second Input (optional) */}
                        {prono.element5FieldSize ? (
                          <div className="flex flex-col items-center">
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              {...form.getInputProps(
                                `predictions.${index}.value2`,
                              )}
                              className="text-slate-900 text-center"
                              style={{
                                width: 50,
                                height: 50,
                                fontSize: 35,
                                textAlign: "center",
                              }}
                              maxLength={prono.element5FieldSize}
                            />
                          </div>
                        ) : null}

                        {/* Right Visual */}
                        <div className="flex flex-col w-[100px] mt-auto">
                          {isValidUrl(prono.element7Visual) &&
                          prono.element7Visual ? (
                            <img
                              src={prono.element7Visual}
                              alt=""
                              className="mx-auto"
                              style={{
                                width: "120px",
                                height: "120px",
                                objectFit: "contain",
                                zIndex: 0,
                              }}
                            />
                          ) : null}
                          <span className="text-center">
                            {renderWithLineBreaks(prono.element6Title)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <CarouselInput
                        items={prono.player}
                        name="selectedItem"
                        form={form}
                        onSelectionChange={(selected) =>
                          form.setFieldValue(
                            `predictions.${index}.value1`,
                            selected,
                          )
                        }
                      />
                    )}
                  </div>
                </React.Fragment>
              );
            } else {
              // Pair rendering - first and second are guaranteed to be input-like
              const firstInput = item.first as PronosticV2Element;
              const secondInput = item.second as PronosticV2Element;
              const { firstIndex, secondIndex } = item;
              return (
                <React.Fragment key={firstInput.index}>
                  <div className="flex flex-col justify-center items-start gap-4 ml-4 mt-8">
                    <h1
                      style={{
                        fontSize:
                          pronosticV2.commonSettings.webApp.moduleSettings
                            .pronoTitleSize,
                        color:
                          pronosticV2.commonSettings.webApp.moduleSettings
                            .pronoTitleColor,
                      }}
                    >
                      {firstInput.element1Title}
                    </h1>
                  </div>

                  <div className="flex flex-col justify-center items-center gap-4 mt-8">
                    <div className="flex items-center gap-5 mx-2">
                      {/* Left Visual (from first) */}
                      <div className="flex flex-col w-[100px] mt-auto">
                        {isValidUrl(firstInput.element2Visual) &&
                        firstInput.element2Visual ? (
                          <img
                            src={firstInput.element2Visual}
                            alt=""
                            className="mx-auto"
                            style={{
                              width: "120px",
                              height: "124px",
                              objectFit: "contain",
                              zIndex: 0,
                            }}
                          />
                        ) : null}
                        <span className="text-center">
                          {renderWithLineBreaks(firstInput.element3Title)}
                        </span>
                      </div>

                      {/* First Input (first pronostic) */}
                      <div className="flex flex-col items-center">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          {...form.getInputProps(
                            `predictions.${firstIndex}.value1`,
                          )}
                          className="text-slate-900 text-center"
                          style={{
                            width: 50,
                            height: 50,
                            fontSize: 35,
                            textAlign: "center",
                          }}
                          maxLength={firstInput.element4FieldSize}
                        />
                      </div>

                      {/* Second Input (second pronostic) */}
                      <div className="flex flex-col items-center">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          {...form.getInputProps(
                            `predictions.${secondIndex}.value1`,
                          )}
                          className="text-slate-900 text-center"
                          style={{
                            width: 50,
                            height: 50,
                            fontSize: 35,
                            textAlign: "center",
                          }}
                          maxLength={secondInput.element4FieldSize}
                        />
                      </div>

                      {/* Right Visual (from second) */}
                      <div className="flex flex-col w-[100px] mt-auto">
                        {isValidUrl(secondInput.element2Visual) &&
                        secondInput.element2Visual ? (
                          <img
                            src={secondInput.element2Visual}
                            alt=""
                            className="mx-auto"
                            style={{
                              width: "120px",
                              height: "120px",
                              objectFit: "contain",
                              zIndex: 0,
                            }}
                          />
                        ) : null}
                        <span className="text-center">
                          {renderWithLineBreaks(secondInput.element3Title)}
                        </span>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            }
          });
        })()}
      </div>

      <h1
        style={{
          fontSize:
            pronosticV2.commonSettings.webApp.moduleSettings.messageTitleSize,
          color:
            pronosticV2.commonSettings.webApp.moduleSettings.messageTitleColor,
          textAlign: "center",
          padding: "20px 10px 10px 10px",
        }}
      >
        {pronosticV2.commonSettings.webApp.texts.footerMessage}
      </h1>
      {Object.keys(form.errors).length > 0 && (
        <Text
          color={
            pronosticV2.commonSettings.webApp.moduleSettings.mandatoryTextColor
          }
          style={{
            fontSize:
              pronosticV2.commonSettings.webApp.moduleSettings
                .mandatoryTextSize,
          }}
        >
          {pronosticV2.commonSettings.webApp.texts.mandatoryMessage}
        </Text>
      )}

      {/* {form.errors && (
        <Text color="whit      <Carousel items={carouselItems} />
e" size="xs">
          Errors:
          {JSON.stringify(form.errors)}
        </Text>
      )} */}

      <Button
        type="submit"
        loading={isLoading}
        style={{
          backgroundColor:
            pronosticV2.commonSettings.webApp.moduleSettings.buttonBgColor,
          color:
            pronosticV2.commonSettings.webApp.moduleSettings.buttonTextColor,
          fontWeight: 100,
        }}
      >
        {pronosticV2.commonSettings.webApp.texts.submitButtonLabel}
      </Button>
    </form>
  );
};
